/**
 * useTheme Hook
 * Custom hook for managing theme mode (light/dark/auto)
 */

import { useState, useEffect, useMemo } from 'react';

export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'ra.theme';

// Get effective theme mode (resolve 'auto' to actual mode)
export function getEffectiveMode(mode: ThemeMode, systemPrefersDark: boolean): 'light' | 'dark' {
  if (mode === 'auto') {
    return systemPrefersDark ? 'dark' : 'light';
  }
  return mode;
}

export function useThemeMode() {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    return saved || 'auto';
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Save to localStorage when changed
  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem(STORAGE_KEY, mode);
  };

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemPrefersDark(e.matches);
    };

    // Prefer addEventListener but fallback to addListener
    if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', handleChange);
    else mediaQuery.addListener(handleChange as any);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', handleChange);
      else mediaQuery.removeListener(handleChange as any);
    };
  }, []);

  // Get effective mode (light or dark)
  const effectiveMode = useMemo(
    () => getEffectiveMode(themeMode, systemPrefersDark),
    [themeMode, systemPrefersDark]
  );

  return {
    themeMode,
    effectiveMode,
    setThemeMode,
    isLight: effectiveMode === 'light',
    isDark: effectiveMode === 'dark',
    isAuto: themeMode === 'auto',
  };
}
